import { mediaList, type MediaItem } from "@/mocks/media";
import { articleList, type ArticleItem } from "@/mocks/articles";
import { specialList, type SpecialItem } from "@/mocks/specials";

export type ContentKind = "media" | "article";

export interface ContentDetail {
  kind: ContentKind;
  id: string;
  title: string;
  cover: string;
  summary: string;
  content: string;
  date: string;
  tags: string[];
  categoryKey: "movie" | "tv" | "star-news";
  media?: MediaItem;
  article?: ArticleItem;
}

export function findMediaById(id: string): MediaItem | undefined {
  return mediaList.find((item) => item.id === id);
}

export function findArticleById(id: string): ArticleItem | undefined {
  return articleList.find((item) => item.id === id);
}

export function findSpecialById(id: string): SpecialItem | undefined {
  return specialList.find((item) => item.id === id);
}

export function getContentById(id: string): ContentDetail | undefined {
  const media = findMediaById(id);
  if (media) {
    return {
      kind: "media",
      id: media.id,
      title: media.title,
      cover: media.cover,
      summary: media.summary,
      content: media.content,
      date: media.releaseDate,
      tags: media.tags,
      categoryKey: media.type,
      media,
    };
  }
  const article = findArticleById(id);
  if (article) {
    return {
      kind: "article",
      id: article.id,
      title: article.title,
      cover: article.cover,
      summary: article.summary,
      content: article.content,
      date: article.date,
      tags: article.tags,
      categoryKey: article.relatedCategory,
      article,
    };
  }
  return undefined;
}

export function resolveContent(id: string): MediaItem | ArticleItem | undefined {
  return findMediaById(id) || findArticleById(id);
}

export interface SearchResult {
  id: string;
  title: string;
  cover: string;
  summary: string;
  kind: ContentKind;
  categoryKey: "movie" | "tv" | "star-news";
  date: string;
  extra: string;
}

export function searchContent(keyword: string): SearchResult[] {
  const q = keyword.trim().toLowerCase();
  if (!q) return [];
  const mediaResults: SearchResult[] = mediaList
    .filter(
      (item) =>
        item.title.toLowerCase().includes(q) ||
        item.summary.toLowerCase().includes(q) ||
        item.genres.some((g) => g.toLowerCase().includes(q)) ||
        item.region.toLowerCase().includes(q) ||
        item.director.toLowerCase().includes(q)
    )
    .map((item) => ({
      id: item.id,
      title: item.title,
      cover: item.cover,
      summary: item.summary,
      kind: "media" as ContentKind,
      categoryKey: item.type,
      date: item.releaseDate,
      extra: `${item.year} · ${item.region} · ${item.genres.join("/")}`,
    }));
  const articleResults: SearchResult[] = articleList
    .filter(
      (item) =>
        item.title.toLowerCase().includes(q) ||
        item.summary.toLowerCase().includes(q) ||
        item.tags.some((t) => t.toLowerCase().includes(q))
    )
    .map((item) => ({
      id: item.id,
      title: item.title,
      cover: item.cover,
      summary: item.summary,
      kind: "article" as ContentKind,
      categoryKey: item.relatedCategory,
      date: item.date,
      extra: item.section === "star" ? "明星动态" : "影视资讯",
    }));
  return [...mediaResults, ...articleResults].sort((a, b) => (a.date < b.date ? 1 : -1));
}

export function getRelatedContent(memberIds: string[], count = 4): (MediaItem | ArticleItem)[] {
  const items = memberIds
    .map((id) => resolveContent(id))
    .filter((item): item is MediaItem | ArticleItem => Boolean(item));
  return items.slice(0, count);
}

export function pickRandomRelated(
  pool: (MediaItem | ArticleItem)[],
  excludeId: string,
  count = 4
): (MediaItem | ArticleItem)[] {
  const candidates = pool.filter((item) => item.id !== excludeId);
  const shuffled = [...candidates].sort(() => Math.random() - 0.5);
  return shuffled.slice(0, count);
}

export function formatDate(date: string): string {
  if (!date) return "";
  const parts = date.split("-");
  if (parts.length === 3) {
    return `${parts[0]}年${Number(parts[1])}月${Number(parts[2])}日`;
  }
  return date;
}

export function isMedia(item: MediaItem | ArticleItem): item is MediaItem {
  return "type" in item;
}